import React from 'react';
import { Container, Grid, Typography, Box, Stack, Chip, Paper } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import CreateSecret from '../Components/CreateSecret';
import Faq from '../Components/Faq';

const trustBadges = [
  { icon: <ShieldOutlinedIcon fontSize="small" />, label: 'AES-256 Encryption' },
  { icon: <VisibilityOffOutlinedIcon fontSize="small" />, label: 'Zero-Knowledge' },
  { icon: <LocalFireDepartmentOutlinedIcon fontSize="small" />, label: 'Self-Destructs' },
  { icon: <TimerOutlinedIcon fontSize="small" />, label: 'Time-Limited' },
];

const steps = [
  {
    icon: <EditOutlinedIcon />,
    title: '1. Write your secret',
    text: 'Type your password, note, or key. It is encrypted in your browser before it ever leaves your device.',
  },
  {
    icon: <LinkOutlinedIcon />,
    title: '2. Share the link',
    text: 'You get a one-time link containing the decryption key. Only someone with the full link can open it.',
  },
  {
    icon: <LocalFireDepartmentOutlinedIcon />,
    title: '3. It self-destructs',
    text: 'After it is read, or once your time or view limit is reached, the secret is permanently deleted.',
  },
];

const Main = () => {
  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          textAlign: 'center',
          pt: { xs: 5, md: 8 },
          pb: { xs: 3, md: 5 },
          px: 2,
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            p: 1.5,
            mb: 2,
            borderRadius: '50%',
            bgcolor: 'action.hover',
          }}
        >
          <ShieldOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        </Box>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontSize: { xs: '2rem', md: '3rem' } }}>
          Share secrets that self-destruct
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ maxWidth: 640, mx: 'auto', fontWeight: 400 }}
        >
          Send passwords, keys, and sensitive notes through an encrypted,
          single-use link. Your secret is encrypted on your device — we can
          never read it.
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          justifyContent="center"
          useFlexGap
          sx={{ mt: 3 }}
        >
          {trustBadges.map((badge) => (
            <Chip
              key={badge.label}
              icon={badge.icon}
              label={badge.label}
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          ))}
        </Stack>
      </Box>

      {/* Create form */}
      <Container maxWidth="md" sx={{ mb: 6 }}>
        <CreateSecret />
      </Container>

      {/* How it works */}
      <Container maxWidth="md" sx={{ mb: 6 }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ mb: 4 }}>
          How it works
        </Typography>
        <Grid container spacing={3}>
          {steps.map((step) => (
            <Grid item xs={12} md={4} key={step.title}>
              <Paper
                variant="outlined"
                sx={{ p: 3, height: '100%', bgcolor: 'background.paper' }}
              >
                <Box sx={{ color: 'primary.main', mb: 1.5 }}>{step.icon}</Box>
                <Typography variant="h6" gutterBottom>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.text}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQ */}
      <Container maxWidth="md">
        <Faq />
      </Container>
    </Box>
  );
};

export default Main;
